import React, { useState } from "react";
import { Box, Button, Typography, CircularProgress, Snackbar, Alert } from "@mui/material";
import * as Tone from "tone";
import { analyze } from "web-audio-beat-detector";
import moment from "moment";
import { useTranslation } from 'react-i18next';

const BPMAnalyzer = ({ onBPM, onDuration }) => {
  const { t } = useTranslation();

  const [bpm, setBPM] = useState(null);
  const [duration, setDuration] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      setError(t('mp3Error'));
      return;
    }

    setError(null);
    setBPM(null);
    setDuration(null);
    setLoading(true);

    try {
      const audioBuffer = await loadAudioWithTone(file);

      const trackDuration = audioBuffer.duration;
      setDuration(trackDuration.toFixed(2));
      onDuration(trackDuration.toFixed(2));

      // Calcular los BPM usando web-audio-beat-detector
      const detectedBPM = await analyze(audioBuffer);
      const normalizedBPM = normalizeBPM(detectedBPM);
      setBPM(Math.round(normalizedBPM)); // Redondear los BPM
      onBPM(Math.round(normalizedBPM));
      
      setSnackbarMessage(`${t('bpmDetected')}: ${Math.round(normalizedBPM)}, ${t('duration')}: ${moment.utc(trackDuration * 1000).format("mm:ss")}`);
      setOpenSnackbar(true);
    } catch (err) {
      setError(t('mp3Error2'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAudioWithTone = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const buffer = await Tone.getContext().rawContext.decodeAudioData(reader.result);
          resolve(buffer);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  };

  const normalizeBPM = (bpm) => {
    // Se filtra el BPM basándose en un umbral
    return bpm > 150 ? bpm / 2 : bpm;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 2,
      }}
    >
      <Typography variant="h6" gutterBottom>
        {t('analyzerTitle')}
      </Typography>
      <Button variant="contained" component="label">
        {t('analyzerButton')}
        <input
          type="file"
          accept="audio/*"
          hidden
          onChange={handleFileUpload}
        />
      </Button>
      {loading && <CircularProgress sx={{ marginTop: 2 }} />}
      {error && (
        <Typography variant="body1" color="error" sx={{ marginTop: 2 }}>
          {error}
        </Typography>
      )}
      {bpm && (
        <Typography variant="body1" sx={{ marginTop: 2 }}>
          {t('detectedBpm')}: {bpm}
        </Typography>
      )}
      {duration && (
        <Typography variant="body1" sx={{ marginTop: 1 }}>
          {t('track_duration')}: {moment.utc(duration * 1000).format("mm:ss")}
        </Typography>
      )}
      {/* Snackbar Notification */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BPMAnalyzer;
